import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { StoryTimeSessionHistoryItem } from '@/types/storytime.types';

interface StoryHistoryListProps {
  sessions: StoryTimeSessionHistoryItem[];
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onSelect: (item: StoryTimeSessionHistoryItem) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onDelete: (sessionId: string) => void;
}

export const StoryHistoryList = ({
  sessions,
  loading,
  error,
  hasMore,
  loadingMore,
  onSelect,
  onRetry,
  onLoadMore,
  onDelete,
}: StoryHistoryListProps) => {
  const { t } = useTranslation('common');

  if (loading) {
    return (
      <Box aria-label={t('storyHistoryLoading')} role="status">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={64} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('storyHistoryRetry')}
          </Button>
        }
      >
        {t('storyHistoryError')}
      </Alert>
    );
  }

  if (sessions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {t('storyHistoryEmpty')}
      </Typography>
    );
  }

  return (
    <Box>
      <List disablePadding>
        {sessions.map((item) => {
          let relativeTime: string;
          try {
            relativeTime = formatDistanceToNow(parseISO(item.created_at), { addSuffix: true });
          } catch {
            relativeTime = '—';
          }
          const metaLabel = `${item.length_band} · ${item.hints ? t('storyHistoryHintsOn') : t('storyHistoryHintsOff')}`;

          return (
            <ListItemButton
              key={item.id}
              onClick={() => onSelect(item)}
              aria-label={t('storyHistoryItemLabel', { topic: item.topic, time: relativeTime })}
              sx={{ minHeight: 44, px: 1, py: 1, borderRadius: 1, mb: 0.5, pr: 5 }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" component="span" fontWeight={500} noWrap>
                      {item.topic}
                    </Typography>
                    <Chip label={item.difficulty_band} size="small" variant="outlined" />
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" component="span">
                      {relativeTime} · {metaLabel}
                    </Typography>
                    {item.story_preview && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        sx={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.story_preview}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <Tooltip title={t('deleteStory', 'Delete story')}>
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={t('deleteStory', 'Delete story')}
                  onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                  sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          );
        })}
      </List>

      {hasMore && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Button
            variant="text"
            size="small"
            onClick={onLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {t('storyHistoryLoadMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
